import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {LmsCardLocationsEnum} from '../../../../settings/lms/lmsHelpers';
import {
  getHasIndividualTrainingAccess,
  getHasWebinarTrainingAccess,
} from '../../../../store/data/company/companySelectors';
import {
  ContactSupportMode,
  getContactSupportMode,
  openContactSupportAction,
} from '../../../../store/panels/app/appPanelActions';
import {communityUrl, helpCenterUrl, webinarTrainingUrl} from '../../../../store/stateless/navigationActions';
import {HelpResourceItem} from '../../../help/helpResourceItem';
import {
  chatWithSupportHelpResourceItem,
  communityHelpResourceItem,
  emailSupportHelpResourceItem,
  helpCenterHelpResourceItem,
  liveTrainingHelpResourceItem,
  messageSupportHelpResourceItem,
} from '../../../help/helpResourceItemContent';

/*
 * Props.
 */

interface AdditionalHelpSectionProps {
  panelId: AppPanelId;
}

/*
 * Style.
 */

const StyledResourcesSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const StyledResourceTitle = styled.h3`
  margin: 0;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledHelpItemsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

/*
 * Component.
 */

export const AdditionalHelpSection: FC<AdditionalHelpSectionProps> = (props) => {
  const {panelId} = props;
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();
  const hasIndividualTrainingAccess = useAppSelector(getHasIndividualTrainingAccess);
  const hasWebinarTrainingAccess = useAppSelector(getHasWebinarTrainingAccess);
  const contactSupportMode = useAppSelector(getContactSupportMode);
  const dispatch = useAppDispatch();

  const onOpenHelpClick = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.HELP_WIDGET_SUPPORT_CLICK);
    dispatch(openContactSupportAction(panelId, contactSupportMode));
  }, [dispatch, panelId, contactSupportMode]);

  return (
    <StyledResourcesSection>
      <StyledResourceTitle>
        <Trans id="help-menu-additional-resources-title">Other resources</Trans>
      </StyledResourceTitle>
      <StyledHelpItemsGrid>
        <HelpResourceItem
          content={communityHelpResourceItem}
          onClick={() => {
            navigateToLink(communityUrl);
            trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.COMMUNITY);
          }}
        />

        {
          // Live webinar is already displayed in Recommended section when 1o1 training is not available.
          hasIndividualTrainingAccess && hasWebinarTrainingAccess && (
            <HelpResourceItem
              content={liveTrainingHelpResourceItem}
              onClick={() => {
                navigateToLink(webinarTrainingUrl);
                trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.LIVE_WEBINAR);
              }}
            />
          )
        }
        <HelpResourceItem
          content={helpCenterHelpResourceItem}
          onClick={() => {
            navigateToLink(helpCenterUrl);
            trackInterfaceEvent(InterfaceEventsEnum.P0_HELP_CENTER_CLICK, {
              location: LmsCardLocationsEnum.P0_HELP_AND_TIPS,
            });
          }}
        />
        <HelpResourceItem content={computeHelpSectionContent(contactSupportMode)} onClick={onOpenHelpClick} />
      </StyledHelpItemsGrid>
    </StyledResourcesSection>
  );
};

const computeHelpSectionContent = (mode: ContactSupportMode) => {
  switch (mode) {
    case ContactSupportMode.FRONT_CHAT:
      return chatWithSupportHelpResourceItem;
    case ContactSupportMode.FRONT_EMAIL:
      return messageSupportHelpResourceItem;
    case ContactSupportMode.NATIVE_EMAIL:
      return emailSupportHelpResourceItem;
    default:
      throw new UnreachableError(mode);
  }
};
